/**
 * Moments on Home (Sep 2026): last released month's DAAily Drivers winners
 * (each with a clap button), "Welcome to DAAily" for new joiners HR chose to
 * announce, and this month's work anniversaries (first three, then "+N more").
 * Rendered as compact rows inside the "This month" card. No dates are shown
 * (or sent): start dates are HR data. Hidden when there's nothing.
 *
 * Clapping is optimistic: the count and state flip at once, the callable
 * confirms (and corrects the count) in the background, a failure rolls back.
 * One clap per person per winner; clicking again takes it back.
 */
import { useRef, useState, type ReactNode } from 'react';
import { CalendarHeart, CalendarClock, PartyPopper, Sparkles } from 'lucide-react';
import type { HubMomentsResponse, MomentWinner } from '@daaily/shared';
import { Icon } from '../design/Icon';
import { overline } from '../design/ui';
import { Medal } from '../drivers/Trophy';
import { monthLabel } from '../drivers/lib/format';
import { callHubClap, patchCachedWinner } from './moments';

function PersonAvatar({ name, photoURL, size = 40 }: { name: string; photoURL: string | null; size?: number }) {
  const [broken, setBroken] = useState(false);
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('');
  const style = { width: size, height: size };
  return photoURL && !broken ? (
    <img src={photoURL} alt="" referrerPolicy="no-referrer" onError={() => setBroken(true)} style={style} className="rounded-full object-cover shrink-0" />
  ) : (
    <span style={style} className="rounded-full bg-surface-3 text-fg-muted text-sm font-medium grid place-items-center shrink-0" aria-hidden="true">{initials}</span>
  );
}

function ClapButton({ winner, onChange }: { winner: MomentWinner; onChange: (w: MomentWinner) => void }) {
  const [pending, setPending] = useState(false);
  const [burst, setBurst] = useState(0);
  const btnRef = useRef<HTMLButtonElement>(null);

  if (winner.isMe) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm text-fg-muted" aria-label={`${winner.claps} claps for you`}>
        <span aria-hidden="true">👏</span>{winner.claps}
      </span>
    );
  }

  const toggle = () => {
    if (pending) return;
    const clap = !winner.clappedByMe;
    const prev = winner;
    const next = { ...winner, clappedByMe: clap, claps: Math.max(0, winner.claps + (clap ? 1 : -1)) };
    onChange(next);
    patchCachedWinner(next.nomineeEmail, next.claps, next.clappedByMe);
    if (clap) {
      setBurst((b) => b + 1);
      btnRef.current?.classList.remove('clap-pop');
      void btnRef.current?.offsetWidth; // restart the pop animation
      btnRef.current?.classList.add('clap-pop');
    }
    setPending(true);
    callHubClap({ month: winner.month, nomineeEmail: winner.nomineeEmail, clap })
      .then((r) => {
        const done = { ...next, claps: r.data.claps, clappedByMe: r.data.clappedByMe };
        onChange(done);
        patchCachedWinner(done.nomineeEmail, done.claps, done.clappedByMe);
      })
      .catch(() => {
        onChange(prev);
        patchCachedWinner(prev.nomineeEmail, prev.claps, prev.clappedByMe);
      })
      .finally(() => setPending(false));
  };

  return (
    <button
      ref={btnRef}
      type="button"
      onClick={toggle}
      aria-pressed={winner.clappedByMe}
      aria-label={winner.clappedByMe ? `Take back your clap for ${winner.displayName}` : `Clap for ${winner.displayName}`}
      className={`relative inline-flex items-center gap-1 h-7 px-2.5 rounded-full border text-xs tabular-nums transition-colors duration-fast ${
        winner.clappedByMe ? 'border-brand/40 bg-brand-subtle text-brand' : 'border-line-strong text-fg-muted hover:bg-surface-2 hover:text-fg'
      }`}
    >
      <span aria-hidden="true">👏</span>
      {winner.claps}
      {burst > 0 && (
        <span key={burst} className="clap-float pointer-events-none absolute left-1/2 -top-1 text-base" aria-hidden="true">👏</span>
      )}
    </button>
  );
}

function WinnerRow({ winner, onChange }: { winner: MomentWinner; onChange: (w: MomentWinner) => void }) {
  return (
    <li className="flex items-center gap-3">
      <div className="relative shrink-0">
        <PersonAvatar name={winner.displayName} photoURL={winner.photoURL} size={32} />
        <span className="absolute -bottom-1.5 -right-2"><Medal valueId={winner.valueId} size={18} label={`${winner.valueName} award`} /></span>
      </div>
      <div className="min-w-0 flex-1 pl-1">
        <p className="text-sm font-medium text-fg truncate">{winner.isMe ? 'You' : winner.displayName}</p>
        <p className="text-xs text-fg-muted truncate">{winner.valueName}</p>
      </div>
      <ClapButton winner={winner} onChange={onChange} />
    </li>
  );
}

/** How many anniversaries show before "+N more". */
const ANNIVERSARIES_SHOWN = 3;

function Group({ icon, title, children }: { icon: typeof Sparkles; title: string; children: ReactNode }) {
  return (
    <div>
      <p className={`${overline} inline-flex items-center gap-1`}><Icon as={icon} size={12} /> {title}</p>
      <div className="mt-2.5">{children}</div>
    </div>
  );
}

/** "Mon 10 Nov" for an ISO date. */
function startLabel(iso: string): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/**
 * The Moments part of Home's "This month" card: compact rows, no cards of
 * their own. Returns null when there is nothing to show.
 */
export function MomentsList({ data }: { data: HubMomentsResponse }) {
  const [winners, setWinners] = useState(data.winners);
  const [allAnniversaries, setAllAnniversaries] = useState(false);
  const { anniversaries, joiners } = data;
  const starters = data.starters ?? [];
  if (winners.length === 0 && anniversaries.length === 0 && joiners.length === 0 && starters.length === 0) return null;
  const update = (w: MomentWinner) => setWinners((ws) => ws.map((x) => (x.nomineeEmail === w.nomineeEmail ? w : x)));
  const shownAnniversaries = allAnniversaries ? anniversaries : anniversaries.slice(0, ANNIVERSARIES_SHOWN);
  const hiddenCount = anniversaries.length - shownAnniversaries.length;

  return (
    <div className="space-y-5">
      {winners.length > 0 && data.winnersMonth && (
        <Group icon={Sparkles} title={`Drivers of ${monthLabel(data.winnersMonth)}`}>
          <ul className="space-y-3">
            {winners.map((w) => <WinnerRow key={w.nomineeEmail} winner={w} onChange={update} />)}
          </ul>
        </Group>
      )}

      {starters.length > 0 && (
        <Group icon={CalendarClock} title="Starting soon">
          <ul className="space-y-2.5">
            {starters.map((s) => (
              <li key={s.email} className="flex items-center gap-3">
                <PersonAvatar name={s.displayName} photoURL={s.photoURL} size={28} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-fg truncate">{s.displayName}</p>
                  <p className="text-xs text-fg-muted truncate">{[s.jobTitle, s.team].filter(Boolean).join(' · ') || 'New colleague'}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-medium text-fg tabular-nums">{s.daysUntil === 1 ? 'tomorrow' : `in ${s.daysUntil} days`}</p>
                  <p className="text-xs text-fg-muted">{startLabel(s.startDate)}</p>
                </div>
              </li>
            ))}
          </ul>
        </Group>
      )}

      {joiners.length > 0 && (
        <Group icon={PartyPopper} title="Welcome to DAAily">
          <ul className="space-y-2.5">
            {joiners.map((j) => (
              <li key={j.email} className="flex items-center gap-3">
                <PersonAvatar name={j.displayName} photoURL={j.photoURL} size={28} />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-fg truncate">{j.displayName}</p>
                  <p className="text-xs text-fg-muted truncate">{[j.jobTitle, j.team].filter(Boolean).join(' · ') || 'New colleague'}</p>
                </div>
              </li>
            ))}
          </ul>
        </Group>
      )}

      {anniversaries.length > 0 && (
        <Group icon={CalendarHeart} title="Anniversaries">
          <ul className="space-y-2.5">
            {shownAnniversaries.map((a) => (
              <li key={a.email} className="flex items-center gap-3">
                <PersonAvatar name={a.displayName} photoURL={a.photoURL} size={28} />
                <p className="min-w-0 text-sm text-fg truncate">
                  <span className="font-medium">{a.displayName}</span>
                  <span className="text-fg-muted"> · {a.years} {a.years === 1 ? 'year' : 'years'}</span>
                </p>
              </li>
            ))}
          </ul>
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => setAllAnniversaries(true)}
              className="mt-2 text-xs text-fg-muted hover:text-fg underline underline-offset-2"
            >
              +{hiddenCount} more
            </button>
          )}
        </Group>
      )}
    </div>
  );
}
